import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Button, message, Form, Input } from 'antd';
import TagTable, { TagTableRef } from '../TagTable';
import './index.scss';
import requirementApi from '@/fetch/bussiness/collectionTask';
import { HttpStatusCode } from '@/fetch/core/constant';
import { debounce } from '@/utils';

const { TextArea } = Input;

// 常量定义
export const PLACEHOLDER_TAG_ID = -1; // 占位标签ID

// 标签数据类型定义
interface TagData {
  tagId: number;
  parentId: number;
  tagName: string;
  tagType: 'MAIN' | 'CHILDREN' | 'DETAIL';
  count: number;
  enabled?: boolean;
  hasSaved?: boolean; // 是否已保存过
}

const CreateRequirement: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [form] = Form.useForm();
  const [tagList, setTagList] = useState<any[]>([]);
  const tagTableRef = useRef<TagTableRef>(null);

  const transformTreeData = (treeData: any) => {
    // 将treeData数组拉平并去掉children属性
    const flattenTreeData = (data: any[]): any[] => {
      const result: any[] = [];
      data.forEach((item) => {
        const { children, ...itemWithoutChildren } = item;
        result.push(itemWithoutChildren);
        if (children && children.length > 0) {
          result.push(...flattenTreeData(children));
        }
      });
      return result;
    };

    const flatData = flattenTreeData(treeData);
    return flatData;
  };
  // 实际的保存逻辑
  const saveRequirement = async () => {
    try {
      // 验证表单
      const formValues = await form.validateFields();
      const treeData = tagTableRef.current?.getTreeData();
      const relatedTags: any = transformTreeData(treeData);
      const validTags = relatedTags.filter(
        (tag: any) => tag.tagId !== PLACEHOLDER_TAG_ID && tag.tagName.trim()
      );

      if (validTags.length === 0) {
        message.warning('请至少添加一个标签');
        return;
      }

      const requirementData = {
        description: formValues.description,
        relatedTags: validTags,
        requiredDetail: formValues.requiredDetail || '',
        forbiddenDetail: formValues.forbiddenDetail || '',
      };

      const res = await requirementApi.createRequirement(requirementData);
      if (res?.code === HttpStatusCode.Success) {
        message.success('需求创建成功');
        onBack();
      } else {
        message.error(res?.message || '需求创建失败');
      }
    } catch (error) {
      console.error('创建需求失败:', error);
    }
  };

  // 使用 debounce 创建防抖的保存函数
  const handleSave = useMemo(
    () => debounce(saveRequirement, 500, true), // 立即执行，500ms防抖
    [form, onBack]
  );

  // 取消操作
  const handleCancel = () => {
    form.resetFields();
    onBack();
  };

  const getTagList = async () => {
    try {
      const res = await requirementApi.getTagList();
      if (res?.code === HttpStatusCode.Success) {
        setTagList(
          res?.data?.map((item: any) => ({
            value: item.tagId,
            label: item.tagName,
          })) || []
        );
      }
    } catch (error) {
      console.error('获取标签列表失败:', error);
    }
  };

  useEffect(() => {
    getTagList();
  }, []);
  return (
    <div className="create-requirement-content">
      <div className="form-section">
        <div className="label-title">
          <i className="icon"></i>
          新建需求
        </div>
        <Form
          form={form}
          layout="horizontal"
          className="requirement-form"
          initialValues={{
            description: '',
            requiredDetail: '',
            forbiddenDetail: '',
          }}
        >
          {/* 需求说明 */}
          <Form.Item
            label="需求说明"
            name="description"
            rules={[
              { required: true, message: '请输入需求说明' },
              { max: 200, message: '需求说明不能超过200个字符' },
            ]}
          >
            <TextArea
              placeholder="对该采集需求进行简要描述"
              maxLength={200}
              showCount
              rows={4}
              className="description-textarea"
            />
          </Form.Item>
          <div className="label-title">添加标签</div>
          <TagTable
            ref={tagTableRef}
            relatedTags={[
              {
                tagId: PLACEHOLDER_TAG_ID,
                parentId: 0,
                tagName: '',
                tagType: 'MAIN',
                count: 0,
                enabled: true,
                hasSaved: false,
              },
            ]}
            loading={false}
            tagOptionList={tagList}
            onRefreshTagList={getTagList}
          />
          {/* 必要事项 */}
          <Form.Item
            label="必要事项"
            name="requiredDetail"
            rules={[{ max: 200, message: '必要事项不能超过200个字符' }]}
          >
            <TextArea
              placeholder="（选填）采集的场景必须满足的条件"
              maxLength={200}
              showCount
              rows={4}
              className="necessary-items-textarea"
            />
          </Form.Item>

          {/* 禁忌事项 */}
          <Form.Item
            label="禁忌事项"
            name="forbiddenDetail"
            rules={[{ max: 200, message: '禁忌事项不能超过200个字符' }]}
          >
            <TextArea
              placeholder="（选填）采集的场景禁止出现的情况"
              maxLength={200}
              showCount
              rows={4}
              className="prohibited-items-textarea"
            />
          </Form.Item>

          {/* 操作按钮 */}
          <Form.Item className="form-actions">
            <div className="footer-buttons">
              <Button className="cancel-btn" onClick={handleCancel}>
                取消
              </Button>
              <Button
                type="primary"
                className="complete-btn"
                onClick={handleSave}
              >
                完成
              </Button>
            </div>
          </Form.Item>
        </Form>
      </div>

      {/* 右侧示意图区域 */}
      <div className="sketch-map-section">{/* 示意图内容 */}</div>
    </div>
  );
};

export default CreateRequirement;
